import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent } from '@/components/ui/shadcn/card';
import { useTranslations } from '@/hooks/useTranslations';
import { cn, useCan } from '@/lib/utils';
import { Link } from '@inertiajs/react';
import { Briefcase, Calendar, ClipboardList, Plus } from 'lucide-react';

interface WorkOrderStats {
    today: number;
    mySchedule: number;
    actionRequired: number;
    forMyReview: number;
    completed: number;
    inProgress: number;
    unscheduled: number;
}

interface ServiceWorkOrdersHomepageProps {
    stats: WorkOrderStats;
    isSupervisor?: boolean;
}

export function ServiceWorkOrdersHomepage({ stats }: ServiceWorkOrdersHomepageProps) {
    const { t } = useTranslations();
    const can = useCan();

    const statCards = [
        {
            icon: Calendar,
            value: stats.today,
            label: t('homepage.service_work_orders.statistics.today', 'Today'),
            iconBg: 'bg-info-subtle',
            iconColor: 'text-info-subtle-foreground',
        },
        {
            icon: ClipboardList,
            value: stats.mySchedule,
            label: t('homepage.service_work_orders.statistics.my_schedule', 'My Schedule'),
            iconBg: 'bg-warning-subtle',
            iconColor: 'text-warning-subtle-foreground',
        },
    ];

    const quickLinks = [
        {
            icon: Briefcase,
            label: t('homepage.service_work_orders.quick_links.all_jobs', 'All Jobs'),
            href: '/work-orders',
        },
        {
            icon: Calendar,
            label: t('homepage.service_work_orders.quick_links.unscheduled_jobs', 'Unscheduled Jobs'),
            href: '/work-orders?unscheduled=true',
        },
    ];

    return (
        <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <h1 className="text-3xl font-bold">{t('homepage.service_work_orders.title', 'Jobs')}</h1>
                {can.can('work-orders.create') && (
                    <Button size="icon" className="h-12 w-12 rounded-full" asChild>
                        <Link href="/work-orders/create">
                            <Plus className="h-6 w-6" />
                        </Link>
                    </Button>
                )}
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-2 gap-4">
                {statCards.map((card, index) => {
                    const Icon = card.icon;
                    return (
                        <Card key={index} className="transition-shadow hover:shadow-md">
                            <CardContent className="flex flex-col items-center justify-center gap-3 p-6">
                                <div className={cn('rounded-lg p-3', card.iconBg)}>
                                    <Icon className={cn('h-6 w-6', card.iconColor)} />
                                </div>
                                <div className="text-center">
                                    <div className="text-3xl font-bold">{card.value}</div>
                                    <div className="text-muted-foreground mt-1 text-sm">{card.label}</div>
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            {/* Quick Links */}
            <Card>
                <CardContent className="p-0">
                    <div className="divide-y">
                        {quickLinks.map((link, index) => {
                            const Icon = link.icon;
                            return (
                                <Link
                                    key={index}
                                    href={link.href}
                                    className="hover:bg-muted/50 group flex items-center justify-between rounded-md p-4 transition-colors"
                                >
                                    <div className="flex items-center gap-3">
                                        <Icon className="text-muted-foreground group-hover:text-foreground h-5 w-5 transition-colors" />
                                        <span className="font-medium">{link.label}</span>
                                    </div>
                                    <span className="text-muted-foreground group-hover:text-foreground transition-colors">→</span>
                                </Link>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
